<template>
    <SkipToContent />
    <div>
        <FullPageLoadingIndicator />
        <styled-modal
            :start-open="showQuizModal"
            ref="modal"
            @on-close="onQuizModalClose"
        >
            <template v-slot:panel="slotProps">
                <div class="flex flex-col gap-2 p-4">
                    <div class="flex gap-2 justify-center">
                        <ExclamationCircleIcon
                            class="h-8 w-8 text-yellow-500"
                        />
                        <h2 class="m-0 mb-4">Special Note</h2>
                    </div>
                    <p class="m-0">
                        The conversions and equivalents tested in this quiz are
                        based on the Apothecaries’ System covered in the video
                        lesson, NOT modern, American standard measures.
                        Therefore, you will need to refer to the equivalents
                        provided in the lesson to successfully complete this
                        quiz.
                    </p>
                    <div class="flex mt-8 gap-2 items-center justify-center">
                        <button
                            @click="slotProps.events?.closeModal()"
                            class="button main rounded-md bg-gray-100 shadow-none border-none hover:bg-gray-200"
                        >
                            Ok, got it!
                        </button>
                    </div>
                </div> </template
            >``
        </styled-modal>
        <Modal />
        <DeprecatedCourseModal
            deprecatedCourseSlug="fundamentals-of-fluoroscopic-imaging"
        />
        <AccessRenewalOfferModal />
        <AccessRenewalModal :user="user" />
        <Toaster />
        <ChatLauncherBottomRight />
        <SiteRibbon />
        <SidebarUser
            v-if="!admin"
            :theme="theme"
            :impersonating="impersonating"
            :features="features"
        />
        <SidebarAdmin v-if="admin" />
        <BladeContent :show-if-blade="true">
            <TitleBar
                :action="action"
                :actionPath="actionPath"
                :app="app"
                :icon="icon"
                :moduleTitle="moduleTitle"
                :resourceTitle="resourceTitle"
                :searchable="searchable"
                :searchAddition="searchAddition"
                :showGlobalSearch="showGlobalSearch"
                :searchClear="searchClear"
                :section="section"
                :sectionPath="sectionPath"
                :showBilling="showBilling"
                :impersonating="impersonating"
            />
        </BladeContent>
        <BladeContent :show-if-blade="false">
            <router-view name="titleBar"></router-view>
        </BladeContent>

        <section class="flex flex-col content pt-17 sm:pt-20 sm:pl-24 h-screen">
            <div class="divide-y divide-black/10">
                <slot name="banner" />
                <AlertBanner
                    v-if="maintenanceBannerEnabled"
                    type="warning"
                    :endDate="maintenanceBannerEndDate"
                    alertName="scheduled-maintenance"
                >
                    <span v-safe-html="maintenanceBannerMessage"></span>
                </AlertBanner>
                <AlertBanner
                    v-if="
                        !accessRenewalFlowEnabled &&
                        accessIsEligible &&
                        !accessCountdown.isExpired &&
                        accessCountdown.days <= 7
                    "
                    type="warning"
                    alertName="expiry-alert-warning"
                >
                    You have
                    <strong
                    >{{ user.access_days_remaining }} days of access
                        remaining.
                    </strong>
                    <a
                        href="/redeem"
                        class="text-blue-600 underline hover:no-underline hover:text-blue-700"
                    >Manage your access codes</a
                    >
                    to continue using the platform.
                </AlertBanner>
                <AlertBanner
                    type="warning"
                    alertName="expiry-alert-warning"
                    v-if="
                        accessRenewalFlowEnabled &&
                        accessIsEligible &&
                        !accessCountdown.isExpired &&
                        accessCountdown.days <= 7
                    "
                >
                    <div
                        class="flex flex-col md:flex-row space-between items-start w-full gap-4"
                    >
                        <div class="flex-1">
                            <strong>
                                Your Clover access ends in
                                {{ accessCountdown.days }} {{ accessCountdown.formattedDate.daysLabel }}
                                <span v-if="accessCountdown.hours > 0">
                                    and
                                    {{ accessCountdown.hours }}
                                    {{ accessCountdown.formattedDate.hoursLabel }}
                                </span>
                            </strong>
                            <br />
                            Don’t lose the progress you’ve worked so hard to
                            build - continue learning without interruption at
                            <strong><em>44% off!</em></strong>
                        </div>
                        <button
                            type="button"
                            class="button small main rounded-md"
                            @click="openAccessRenewalModal"
                        >
                            Claim Deal & Continue Learning
                        </button>
                    </div>
                </AlertBanner>
                <AlertBanner
                    v-if="accessIsEligible && accessCountdown.isExpired"
                    type="danger"
                    alert-name="expiry-alert-expired"
                >
                    <strong>Your access code has expired! </strong>
                    <a href="/redeem" class="text-blue-600 underline"
                        >Redeem a new access code</a
                    >
                    or
                    <a
                        href="/account/checkout/select-plan"
                        class="text-blue-600 underline"
                        >purchase a subscription</a
                    >
                    to continue using the platform.
                </AlertBanner>
            </div>

            <GlobalFilterDropdown
                v-if="app === 'Learn'"
                class="flex sm:hidden border-b border-gray-200"
            />

            <div
                class="flex-grow py-4 sm:py-8"
                :class="{
                    'px-8': wide,
                    container: !wide,
                }"
            >
                <PageTitle
                    v-if="app !== 'Learn' && app !== 'Home'"
                    class="flex justify-center sm:hidden border-b border-gray-200 mb-6 pb-6"
                    v-bind="{
                        action,
                        actionPath,
                        app,
                        icon,
                        section,
                        sectionPath,
                    }"
                />
                <main id="main" tabindex="-1">
                    <slot />
                </main>
            </div>
            <slot name="footer" />
        </section>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";
import BladeContent from "../BladeContent.vue";
import SkipToContent from "../_elements/SkipToContent.vue";
import Modal from "../_elements/modals/Modal.vue";
import Toaster from "../_elements/Toaster.vue";
import ChatLauncherBottomRight from "../_elements/ChatLauncherBottomRight.vue";
import SiteRibbon from "../_elements/SiteRibbon.vue";
import SidebarUser from "./SidebarUser.vue";
import SidebarAdmin from "./SidebarAdmin.vue";
import TitleBar from "./TitleBar.vue";
import PageTitle from "../_elements/PageTitle.vue";
import FullPageLoadingIndicator from "@/components/ui/loaders/FullPageLoadingIndicator.vue";
import WelcomeModal from "../Dashboard/WelcomeModal.vue";
import StyledModal from "../_elements/modals/StyledModal.vue";
import SuccessModal from "../Dashboard/SuccessModal.vue";
import FloodgateModal from "../Dashboard/FloodgateModal.vue";
import DeprecatedCourseModal from "../Dashboard/DeprecatedCourseModal.vue";
import { analytics } from "@/services/segment.ts";
import AlertBanner from "@/components/_elements/alerts/AlertBanner.vue";
import {
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
} from "@heroicons/vue/20/solid";
import AnnouncementModal from "../Dashboard/AnnouncementModal.vue";
import { getEnv } from "@/env";
import AccessRenewalOfferModal from "../_elements/modals/AccessRenewalOfferModal.vue";
import AccessRenewalModal from "../_elements/modals/AccessRenewalModal.vue";
import { useAccessRenewalModalStore } from "@/stores/accessRenewalModalStore";
import { useAcademicStudentAccessExpiration } from "@/hooks/useAcademicStudentAccessExpiration";
import GlobalFilterDropdown from "@/components/Learn/GlobalFilterDropdown.vue";

export default defineComponent({
    setup(props) {
        const { countdown, isEligible } = useAcademicStudentAccessExpiration(props.user);
        return { accessCountdown: countdown, accessIsEligible: isEligible };
    },
    components: {
        AlertBanner,
        FullPageLoadingIndicator,
        GlobalFilterDropdown,
        PageTitle,
        TitleBar,
        SidebarAdmin,
        SidebarUser,
        SiteRibbon,
        Toaster,
        ChatLauncherBottomRight,
        BladeContent,
        SkipToContent,
        Modal,
        WelcomeModal,
        FloodgateModal,
        StyledModal,
        SuccessModal,
        DeprecatedCourseModal,
        ExclamationTriangleIcon,
        ExclamationCircleIcon,
        AnnouncementModal,
        AccessRenewalOfferModal,
        AccessRenewalModal,
    },
    props: {
        action: String,
        actionPath: String,
        active: String,
        admin: Boolean,
        app: String,
        icon: String,
        learnerMode: Boolean,
        moduleTitle: String,
        resourceTitle: String,
        searchable: Array,
        searchAddition: String,
        searchClear: Boolean,
        section: String,
        sectionPath: String,
        showGlobalSearch: {
            type: Boolean,
            default: false,
        },
        showBilling: Boolean,
        theme: String,
        user: Object as PropType<any>,
        permissions: Object,
        settings: Object,
        websitePath: String,
        wide: Boolean,
        impersonating: Boolean,
        features: Object,
    },
    computed: {
        showQuizModal() {
            return (
                window.location.href.includes("apothecaries-system-quiz") &&
                sessionStorage.getItem("apothecaries-quiz-modal") !== "false"
            );
        },
        isManager() {
            const roles = [
                "admin",
                "admin-view-client-reports",
                "panorama-admin",
                "partner-owner",
                "partner-admin",
                "group-owner",
                "group-admin",
            ];
            // if user.role is in roles
            return roles.includes(this.user.role);
        },
        showWebinarBanner() {
            const now = new Date(); // Get current date and time in client's local timezone
            // June 16th, 00:00:00 EDT (04:00:00 UTC)
            const startDateTimeUTC = Date.UTC(
                now.getFullYear(),
                5,
                16,
                4,
                0,
                0
            );
            // June 25th, 11:00:00 EDT (15:00:00 UTC)
            const endDateTimeUTC = Date.UTC(now.getFullYear(), 5, 25, 15, 0, 0);
            // Get the current time in milliseconds since epoch (UTC)
            const currentTimestampUTC = now.getTime(); // getTime() returns milliseconds since epoch, which is inherently UTC
            // Compare the current UTC timestamp with the defined UTC start and end timestamps
            return (
                currentTimestampUTC >= startDateTimeUTC &&
                currentTimestampUTC < endDateTimeUTC
            );
        },
        maintenanceBannerEnabled() {
            return getEnv('VITE_MAINTENANCE_BANNER_ENABLED') === "true";
        },
        maintenanceBannerEndDate() {
            return getEnv('VITE_MAINTENANCE_BANNER_END_DATE') || "";
        },
        maintenanceBannerMessage() {
            return getEnv('VITE_MAINTENANCE_BANNER_MESSAGE') || "";
        },
        accessRenewalFlowEnabled() {
            return getEnv('VITE_ENABLE_ACCESS_RENEWAL_FLOW') === "true";
        },
        shouldShowAccessRenewalOfferModal() {
            return this.accessRenewalFlowEnabled && this.accessIsEligible && !this.accessCountdown.isExpired && this.accessCountdown.days <= 7;
        },
    },
    methods: {
        onQuizModalClose: function () {
            sessionStorage.setItem("apothecaries-quiz-modal", "false");
        },
        openAccessRenewalModal: function () {
            const renewalModalStore = useAccessRenewalModalStore();
            renewalModalStore.open();
        },
    },
    created() {
        analytics.page();
        this.user.permissions = this.permissions;
        this.user.settings = this.settings;
        this.user.learnerMode = this.learnerMode;

        // @ts-ignore
        this.$store.commit("registerState", {
            activePath: this.active,
            token: (document.querySelector('meta[name="token"]') as any)
                .content,
            user: this.user,
            websitePath: this.websitePath,
        });
    },
    mounted() {
        if (this.shouldShowAccessRenewalOfferModal) {
            const modalStore = useAccessRenewalModalStore();
            if (modalStore.shouldShowModal(this.user.uuid)) {
                analytics.track('B2B to D2C Eligibility Triggered', {
                    product: this.user.subscription_plan,
                    days_remaining: this.accessCountdown.days,
                });

                this.$nextTick(() => {
                    modalStore.open();
                });
            }
        }

        // Fire banner_viewed once per session when the b2b renewal banner is visible
        const bannerSessionKey = 'b2bd2c_banner_viewed';
        if (
            this.accessRenewalFlowEnabled &&
            this.accessIsEligible &&
            !this.accessCountdown.isExpired &&
            this.accessCountdown.days <= 7 &&
            !sessionStorage.getItem(bannerSessionKey)
        ) {
            analytics.track('B2B to D2C Banner Viewed', {
                product: this.user.subscription_plan,
                days_remaining: this.accessCountdown.days,
            });
            sessionStorage.setItem(bannerSessionKey, '1');
        }
    },
});
</script>
